import { ChangeDetectionStrategy, Component, input, output, signal, WritableSignal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TicketAdminFilterKey, ZoudashUtils } from '@workspace/data-types';
import {
  AbstractSignalFormComponent,
  DataHeaderFilter,
  FilterChangedDetection,
  FilterRecord,
} from '@workspace/ng-aura';
import { TicketColumnKey } from '../abstract-tickets-data-table.component';

export interface TicketFilterModel {
  priority: string | string[] | null;
  status: string | string[] | null;
}

@Component({
  selector: 'core-tickets-data-table-filter',
  templateUrl: './tickets-data-table-filter.component.html',
  imports: [FormField, MatFormFieldModule, MatInputModule, MatListModule, MatSlideToggleModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketsDataTableFilterCoreComponent extends AbstractSignalFormComponent<
  Partial<Record<TicketAdminFilterKey, string | string[]>>,
  TicketFilterModel
> {
  currentFilterView = input<string | null>(null);
  filters = input.required<DataHeaderFilter<TicketColumnKey>[]>();
  filterChanged = output<FilterChangedDetection>();

  priorityOptions = [
    { value: 'low', label: 'Basse' },
    { value: 'normal', label: 'Normale' },
    { value: 'high', label: 'Haute' },
  ];

  statusOptions = [
    { value: 'new', label: 'Nouveau' },
    { value: 'open', label: 'Ouvert' },
    { value: 'closed', label: 'Fermé' },
  ];

  ticketAdminFilterKey = TicketAdminFilterKey;

  override model: WritableSignal<TicketFilterModel> = signal<TicketFilterModel>({
    priority: null,
    status: null,
  });

  override formTree = form(this.model);

  override emitDto(newDto: Partial<Record<TicketAdminFilterKey, string | string[]>>): void {
    this.filterChanged.emit({
      filter: newDto as FilterRecord,
      changed: !ZoudashUtils.isEqual(newDto, this.ogValue),
    });
  }

  protected override buildModelFromDto(
    dto: Partial<Record<TicketAdminFilterKey, string | string[]>>,
  ): TicketFilterModel {
    return {
      priority: dto?.[TicketAdminFilterKey.Priority] ?? null,
      status: dto?.[TicketAdminFilterKey.Status] ?? null,
    };
  }

  protected override buildDtoFromModel(
    model: TicketFilterModel,
  ): Partial<Record<TicketAdminFilterKey, string | string[]>> {
    const dto: Partial<Record<TicketAdminFilterKey, string | string[]>> = {
      priority: model.priority ?? undefined,
      status: model.status ?? undefined,
    };
    return Object.fromEntries(Object.entries(dto).filter(([_, v]) => v != null)) as Partial<
      Record<TicketAdminFilterKey, string | string[]>
    >;
  }
}
